// Images inside a blog post's body — what the editor's image button, a pasted
// screenshot and a dropped photo all go through.
//
// ⚠️ The body is stored as HTML and the website renders it as it is, so an
// image must be UPLOADED and written in as the permanent link the server
// answers (vc_be utils/cmsBlogImage). Quill's own default embeds the file as
// base64: one phone photo is megabytes inside the post, over the save's
// request limit, so the whole post fails to save.

import { uploadCmsImage } from "../../utils/cmsAdminApi";
import { shrinkImageToFit } from "../../utils/shrinkImage";

// What the body editor allows (Quill's `formats`). Here rather than in the
// editor so the tests run Quill with the same list: a format left off it is
// dropped from the post without a word.
export const BODY_FORMATS = [
  "header", "bold", "italic", "underline", "strike", "code",
  "list", "indent", "blockquote", "code-block", "link", "image",
];

export const BLOG_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
export const BLOG_IMAGE_ACCEPT = BLOG_IMAGE_TYPES.join(",");

// A photo over this is resized before it goes up: the website column is under
// 800px wide, and a 5 MB camera original is the slowest thing on the page.
const SHRINK_ABOVE_BYTES = 1024 * 1024;
// What the server takes (vc_be middleware/fileUpload imageFile).
const SERVER_MAX_BYTES = 10 * 1024 * 1024;

const mb = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Upload one image for the body. Resolves to its permanent URL, or throws an
 * Error whose message is for the author.
 */
export async function uploadBlogImage(file) {
  if (!file) throw new Error("Choose an image to upload.");
  if (!BLOG_IMAGE_TYPES.includes(file.type)) throw new Error("Upload a PNG, JPG, WebP or GIF image.");

  let toSend = file;
  if (file.size > SHRINK_ABOVE_BYTES) {
    // Best effort: an animated GIF, or a browser that cannot re-encode, sends the original.
    try { toSend = (await shrinkImageToFit(file, SHRINK_ABOVE_BYTES)) || file; } catch { toSend = file; }
  }
  if (toSend.size > SERVER_MAX_BYTES) {
    throw new Error(`That image is ${mb(toSend.size)} — the limit is 10 MB. Resize or compress it and try again.`);
  }

  try {
    const res = await uploadCmsImage(toSend, "blog-image");
    if (!res?.url) throw new Error("no url");
    return res.url;
  } catch (err) {
    throw new Error(err?.response?.data?.message || "The image could not be uploaded. Please try again.");
  }
}

// A line's own formats that an image line must not keep — an image is not a
// heading, a list item or a quote, and a code block cannot hold one at all
// (Quill drops it without a word).
const LINE_FORMATS = ["header", "list", "blockquote", "indent", "code-block"];

/**
 * Put an image on a line of its own at `index` — the website draws a body image
 * as a block, so one dropped mid-sentence would split the sentence anyway.
 * Returns where the caret belongs afterwards: the start of the line below.
 *
 * Done with Quill's own calls rather than one delta: `insertText("\n")` splits a
 * line the way Enter does, keeping each half's heading or list, where a bare
 * newline in a delta would hand the heading to the image's line.
 */
export function insertImageAt(quill, index, { url, alt = "" }) {
  let at = index;
  const [, offset] = quill.getLine(at);
  if (offset > 0) {
    quill.insertText(at, "\n", "user");
    at += 1;
  }
  // `at` is now the start of a line. Give the image an empty one, cleared of
  // those formats first — inside a code block the image would be dropped.
  if (quill.getText(at, 1) !== "\n") quill.insertText(at, "\n", "user");
  const kept = quill.getFormat(at, 1);
  const reset = Object.fromEntries(LINE_FORMATS.filter((f) => kept[f]).map((f) => [f, false]));
  if (Object.keys(reset).length) quill.formatLine(at, 1, reset, "user");

  quill.insertEmbed(at, "image", url, "user");
  if (alt) quill.formatText(at, 1, "alt", alt, "user");

  // The last line of the post: add one below, or there is nowhere to type.
  if (at + 1 >= quill.getLength() - 1) quill.insertText(at + 1, "\n", "user");
  const after = at + 2;
  quill.setSelection(after, 0, "user");
  return after;
}

/** The image at `index` in the editor, or null. */
export function imageAt(quill, index) {
  if (index == null || index < 0) return null;
  const [op] = quill.getContents(index, 1).ops;
  if (!op || typeof op.insert !== "object" || !op.insert.image) return null;
  return { index, src: op.insert.image, alt: op.attributes?.alt || "" };
}

/** Swap the image at `index` for another, keeping its place. */
export function replaceImageAt(quill, index, { url, alt = "" }) {
  quill.deleteText(index, 1, "user");
  quill.insertEmbed(index, "image", url, "user");
  if (alt) quill.formatText(index, 1, "alt", alt, "user");
}

/** Change only the description of the image at `index`. */
export function describeImageAt(quill, index, alt) {
  quill.formatText(index, 1, "alt", alt || false, "user");
}

/** How many images the body holds, and how many have no description. */
export function countImages(html) {
  const tags = String(html || "").match(/<img\b[^>]*>/gi) || [];
  const missingAlt = tags.filter((tag) => !/\balt\s*=\s*("[^"]*\S[^"]*"|'[^']*\S[^']*')/i.test(tag)).length;
  return { total: tags.length, missingAlt };
}

const DATA_IMAGE = /(<img\b[^>]*?\bsrc\s*=\s*")(data:(image\/(?:png|jpeg|webp|gif));base64,([A-Za-z0-9+/=\s]+))"/gi;

function dataUrlToFile(type, base64, n) {
  const bytes = atob(base64.replace(/\s+/g, ""));
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) buf[i] = bytes.charCodeAt(i);
  const ext = type === "image/jpeg" ? "jpg" : type.split("/")[1];
  return new File([buf], `pasted-image-${n}.${ext}`, { type });
}

/**
 * Images that arrived as base64 inside pasted HTML (copied from a document or
 * another page together with its text): upload each and write its link in.
 * Resolves to `{ html, count }` — the body unchanged when there were none.
 * Throws when one cannot be uploaded, so the save stops rather than failing on
 * a body over the size limit.
 */
export async function uploadPastedImages(html, upload = uploadBlogImage) {
  const body = String(html || "");
  const found = new Map();
  for (const m of body.matchAll(DATA_IMAGE)) {
    if (!found.has(m[2])) found.set(m[2], { type: m[3], base64: m[4] });
  }
  if (!found.size) return { html: body, count: 0 };

  const urls = new Map();
  let n = 0;
  for (const [dataUrl, { type, base64 }] of found) {
    n += 1;
    urls.set(dataUrl, await upload(dataUrlToFile(type, base64, n)));
  }
  const next = body.replace(DATA_IMAGE, (whole, head, dataUrl) => `${head}${urls.get(dataUrl)}"`);
  return { html: next, count: found.size };
}
